import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
} from "@mui/material";
import { CLUB_COUNTRIES, isLuxembourgClubCountry, isLuxembourgFederationClubId } from "../../constants/countries";
import { readStoredActiveClubId } from "../../utils/switchActiveClubClient";
import { createNewcomer } from "../funtionsBD/Mainservice";
import { NewcomerDossierPanel } from "./NewcomerDossierPanel";

function yearsOld(iso) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!match) return null;
  const today = new Date();
  let age = today.getFullYear() - Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day)) age -= 1;
  return age;
}

export function AddNewcomerDialog({ open, onClose, clubId, tsId, onCreated }) {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const country = user.club?.country || user.club_details?.country || user.country;
  const activeClub = clubId || readStoredActiveClubId(user) || user.club_id;
  const luxembourg = isLuxembourgClubCountry(country) || isLuxembourgFederationClubId(country, activeClub);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    eu_citizen: "",
    birth_country: "",
    parental_registration: false,
    parent_email: "",
    sex: "",
    birth_date: "",
    licence_type: "joueur",
    matricule: "",
    nationality: "",
    residence_country: "Luxembourg",
    postal_code: "",
    locality: "",
    house_number: "",
    street: "",
    phone: "",
    previous_member: "",
    previous_club: "",
    previous_federation: "",
    previous_year_out: "",
    tutor_name: "",
    tutor_relation: "",
    tutor_email: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);

  const set = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (luxembourg) {
        const age = yearsOld(form.birth_date);
        const tutorNeeded = form.parental_registration || (age != null && age < 18);
        const missing = [];
        if (!form.sex) missing.push("sexe");
        if (!form.birth_date) missing.push("date de naissance");
        if (!form.matricule.trim()) missing.push("matricule");
        if (!form.nationality.trim()) missing.push("nationalité");
        if (!form.residence_country || !form.postal_code.trim() || !form.locality.trim() || !form.house_number.trim() || !form.street.trim()) {
          missing.push("adresse");
        }
        if (!form.phone.trim()) missing.push("téléphone");
        if (!form.previous_member) missing.push("club ou fédération précédent");
        if (form.previous_member === "yes" && !form.previous_club.trim()) missing.push("nom du club précédent");
        if (tutorNeeded && (!form.tutor_name.trim() || !form.tutor_relation.trim() || !form.tutor_email.trim())) {
          missing.push("tuteur");
        }
        if (missing.length) {
          setError(`Still needed for the FLTT form: ${missing.join(", ")}`);
          setBusy(false);
          return;
        }
      }
      const [birthYear, birthMonth, birthDay] = (form.birth_date || "").split("-");
      const payload = await createNewcomer({
        ...form,
        eu_citizen: form.eu_citizen === "" ? null : form.eu_citizen === "yes",
        club_id: activeClub,
        ts_id: tsId || undefined,
        affiliation: luxembourg ? {
          licence_type: form.licence_type,
          birth_name: form.last_name,
          usage_first_name: form.first_name,
          sex: form.sex,
          birth_year: birthYear || "",
          birth_month: birthMonth || "",
          birth_day: birthDay || "",
          matricule: form.matricule,
          nationality: form.nationality,
          residence_country: form.residence_country,
          postal_code: form.postal_code,
          locality: form.locality,
          house_number: form.house_number,
          street: form.street,
          email: form.email,
          phone: form.phone,
          previous_member: form.previous_member,
          previous_club: form.previous_member === "yes" ? form.previous_club : "",
          previous_federation: form.previous_member === "yes" ? form.previous_federation : "",
          previous_year_out: form.previous_member === "yes" ? form.previous_year_out : "",
          tutor_name: form.tutor_name,
          tutor_relation: form.tutor_relation,
          tutor_email: form.tutor_email,
        } : undefined,
      });
      setCreated(payload.data);
      if (onCreated) onCreated(payload.data);
    } catch (err) {
      setError(err?.message || "Could not add this newcomer");
    } finally {
      setBusy(false);
    }
  };

  const needsTutor = form.parental_registration || (yearsOld(form.birth_date) != null && yearsOld(form.birth_date) < 18);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={created ? "md" : "sm"}>
      <DialogTitle>Add newcomer</DialogTitle>
      <DialogContent>
        {created ? (
          <>
            <p>
              {created.person?.first_name} is on the club list. The document link was sent to {created.person?.email}.
            </p>
            <p>
              <a href={created.dossier_url}>{created.dossier_url}</a>
            </p>
            <NewcomerDossierPanel mode="staff" dossierId={created.dossier_id} />
          </>
        ) : (
          <form id="add-newcomer" onSubmit={submit}>
            <TextField label="First name" value={form.first_name} onChange={set("first_name")} required fullWidth margin="dense" />
            <TextField label="Last name" value={form.last_name} onChange={set("last_name")} required fullWidth margin="dense" />
            <TextField label="Email" type="email" value={form.email} onChange={set("email")} required fullWidth margin="dense" />
            {luxembourg ? (
              <>
                <TextField select label="Citizen of the European Union" value={form.eu_citizen} onChange={set("eu_citizen")} fullWidth margin="dense">
                  <MenuItem value="">Not answered yet</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
                <TextField select label="Country of birth" value={form.birth_country} onChange={set("birth_country")} fullWidth margin="dense">
                  <MenuItem value="">Not answered yet</MenuItem>
                  {CLUB_COUNTRIES.map((countryOption) => (
                    <MenuItem key={countryOption.code} value={countryOption.code}>{countryOption.name}</MenuItem>
                  ))}
                </TextField>
                <TextField select label="A parent is registering this child" value={form.parental_registration ? "yes" : "no"} onChange={(event) => setForm((prev) => ({ ...prev, parental_registration: event.target.value === "yes" }))} fullWidth margin="dense">
                  <MenuItem value="no">No</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                </TextField>
                {form.parental_registration ? (
                  <TextField label="Parent email" type="email" value={form.parent_email} onChange={set("parent_email")} fullWidth margin="dense" />
                ) : null}
                <p>These details are printed on the FLTT form. The player then only prints it and signs it.</p>
                <TextField select label="Type de licence" value={form.licence_type} onChange={set("licence_type")} fullWidth margin="dense" required>
                  <MenuItem value="joueur">Joueur</MenuItem>
                  <MenuItem value="loisir">Loisir</MenuItem>
                </TextField>
                <TextField select label="Sexe" value={form.sex} onChange={set("sex")} fullWidth margin="dense" required>
                  <MenuItem value="">Choose</MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="F">F</MenuItem>
                </TextField>
                <TextField label="Date de naissance" type="date" value={form.birth_date} onChange={set("birth_date")} fullWidth margin="dense" required InputLabelProps={{ shrink: true }} />
                <TextField label="Matricule" value={form.matricule} onChange={set("matricule")} fullWidth margin="dense" required helperText="13 digits on the Luxembourg identity card" />
                <TextField label="Nationalité" value={form.nationality} onChange={set("nationality")} placeholder="luxembourgeoise" fullWidth margin="dense" required />
                <TextField select label="Pays" value={form.residence_country} onChange={set("residence_country")} fullWidth margin="dense" required>
                  {CLUB_COUNTRIES.map((countryOption) => (
                    <MenuItem key={countryOption.code} value={countryOption.name}>{countryOption.name}</MenuItem>
                  ))}
                </TextField>
                <TextField label="Code postal" value={form.postal_code} onChange={set("postal_code")} fullWidth margin="dense" required />
                <TextField label="Localité" value={form.locality} onChange={set("locality")} fullWidth margin="dense" required />
                <TextField label="N° maison" value={form.house_number} onChange={set("house_number")} fullWidth margin="dense" required />
                <TextField label="Rue" value={form.street} onChange={set("street")} fullWidth margin="dense" required />
                <TextField label="N° tél." value={form.phone} onChange={set("phone")} fullWidth margin="dense" required />
                <TextField select label="Already a member of another club or federation?" value={form.previous_member} onChange={set("previous_member")} fullWidth margin="dense" required>
                  <MenuItem value="">Choose</MenuItem>
                  <MenuItem value="no">No — leave those lines blank</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                </TextField>
                {form.previous_member === "yes" ? (
                  <>
                    <TextField label="Club précédent" value={form.previous_club} onChange={set("previous_club")} fullWidth margin="dense" required />
                    <TextField label="Fédération du club précédent" value={form.previous_federation} onChange={set("previous_federation")} fullWidth margin="dense" />
                    <TextField label="Année de sortie" value={form.previous_year_out} onChange={set("previous_year_out")} fullWidth margin="dense" />
                  </>
                ) : null}
                {needsTutor ? (
                  <>
                    <TextField label="Tuteur — nom et prénom" value={form.tutor_name} onChange={set("tutor_name")} fullWidth margin="dense" required />
                    <TextField label="Relation" value={form.tutor_relation} onChange={set("tutor_relation")} placeholder="Père, Mère…" fullWidth margin="dense" required />
                    <TextField label="E-mail du tuteur" type="email" value={form.tutor_email} onChange={set("tutor_email")} fullWidth margin="dense" required />
                  </>
                ) : null}
              </>
            ) : null}
            {error ? <p>{error}</p> : null}
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{created ? "Close" : "Cancel"}</Button>
        {created ? null : (
          <Button type="submit" form="add-newcomer" variant="contained" disabled={busy}>
            {busy ? "Saving…" : "Add and send the link"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function NewcomerDossierDialog({ open, dossierId, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogContent>
        {open && dossierId ? <NewcomerDossierPanel mode="staff" dossierId={dossierId} /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
